// CATÁLOGO ÚNICO de servicios de la SUITE Alebas — UNA sola base para AMBAS plataformas
// (Solar Hub + FluxAI). El login (en cualquiera de las dos apps) autentica los servicios que
// el usuario tiene CONTRATADOS en toda la suite: el `api/` de FluxAI es la base única y emite el
// JWT con `servicios[]`; las dos apps leen esa MISMA lista para gatear (un solo soporte, una
// sola fuente de verdad de lo contratado). Los SKUs están alineados con FluxAI (SQL 95).
//
// Cada servicio declara:
//   sku             identificador estable (la MISMA llave en las dos plataformas)
//   nombre · desc · icon
//   tier            'gratis' | 'premium'
//   requiereContrato true si necesita estar en `servicios[]` del usuario (entitlement)
//   audiencia       qué audiencias lo ven/contratan (user/technician/supplier/operator/admin; null=invitado)
//   suite           'solarhub' | 'fluxai'  (en qué plataforma se PRESTA; se consume vía deep-link/SSO)
//   ruta            vista de Solar Hub que abre (App.jsx view) o null
//
// Qué se CONSUME en el lanzamiento (capas). No se mezclan:
//   publico  — cotizador gratis (entra cualquiera)
//   estudio  — ingeniería de detalle (de pago)
//   campo    — técnico RETIE y proveedor (no son SKU del cliente final)
//   ami      — Solar Hub pide equipos+ficha; la consola AMI vive en FluxAI
// Balance (`red`) y el cupo por equipo NO van aquí: son exclusivos de FluxAI.

export const CAPAS_SERVICIO = {
  publico: { titulo: 'Cotización gratuita', sub: 'Entra, cotiza, te queda la propuesta' },
  estudio: { titulo: 'Estudio fotovoltaico', sub: 'Ingeniería de detalle · de pago' },
  campo: { titulo: 'Instalación y suministro', sub: 'Técnicos RETIE y proveedores' },
  ami: { titulo: 'Telemetría y planta', sub: 'Equipos y fichas · la consola AMI vive en FluxAI' },
};

export const SERVICE_CATALOG = [
  {
    sku: 'cotizacion_rapida', nombre: 'Cotización rápida', icon: '', tier: 'gratis', capa: 'publico',
    desc: 'Cotiza con techo real (Google Solar), PVGIS y tarifa CREG. Gratis.',
    audiencia: ['user', 'technician', 'operator', 'admin', null], ruta: 'quoter', suite: 'solarhub',
  },
  {
    // SERVICIO PRINCIPAL de Solar Hub: la herramienta de ÚLTIMO NIVEL, NATIVA en Solar Hub (no
    // embebe FluxAI; CONSUME su dato real). Es el Cotizador en modo ALTO NIVEL: techo Google Solar
    // + telemetría real (medido vs simulado) + ingeniería de detalle. El "Cotizador" web es aparte.
    sku: 'solar_study', nombre: 'Estudio fotovoltaico', icon: '', tier: 'premium', requiereContrato: true, principal: true, capa: 'estudio',
    desc: 'Ingeniería de detalle (8760 h, pérdidas, P50/P90, unifilar, memoria). El técnico lo tiene siempre: es el kit de último nivel para cerrar.',
    // 🔴 `ruta: 'estudio'` y NO `abreFlux`. El embebido exige que el token de la suite reanude
    // sesión en FluxAI, y hoy `mintToken` emite `alg:none`: su `ingestSsoToken` solo acepta un
    // token sin firma para tres correos de su allowlist → para el resto no hay sesión, su
    // AuthGuard manda a /login y el usuario aterriza en un login ajeno. La vista `estudio` ya
    // existe (`App.jsx` monta el Quoter con `alto`), así que el estudio nativo NO depende de SSO.
    audiencia: ['user', 'technician', 'operator', 'admin', null], ruta: 'estudio', suite: 'solarhub',
  },
  {
    sku: 'monitoreo_premium', nombre: 'Monitoreo de planta', icon: '', tier: 'premium', requiereContrato: true, capa: 'ami',
    desc: 'Equipos asignados y su ficha. Solar Hub pide eso a FluxAI; no abre SCADA ni el dashboard.',
    consume: ['equipos', 'ficha'],
    audiencia: ['user', 'technician', 'operator', 'admin'], ruta: 'monitoreo', suite: 'solarhub',
  },
  {
    // El informe ISO 50001 tiene DOS niveles:
    //  - ESTIMADO: desde la cotización (sin telemetría) → parte del estudio, abierto/premium normal.
    //  - COMPLETO (M&V medido-vs-simulado): SOLO si el usuario tiene equipos en telemetría FluxAI.
    sku: 'informe_iso', nombre: 'Reportes ISO 50001 (IA)', icon: '', tier: 'premium', capa: 'ami', grupo: 'ampliar',
    desc: 'Auditoría energética profesional. La versión completa (medido vs simulado) requiere telemetría FluxAI.',
    requiere: 'monitoreo_premium',
    audiencia: ['user', 'technician', 'operator', 'admin'], ruta: null, suite: 'fluxai',
  },
  {
    sku: 'facturacion', nombre: 'Facturación de energía (CREG)', icon: '', tier: 'premium', requiereContrato: true, capa: 'ami', grupo: 'ampliar',
    desc: 'Simula y emite facturas con la tarifa regulada; emisión por lote desde lecturas.',
    audiencia: ['user', 'operator', 'admin'], ruta: null, suite: 'fluxai',
  },
  {
    sku: 'scada', nombre: 'SCADA — Proceso', icon: '', tier: 'premium', requiereContrato: true, capa: 'ami', grupo: 'ampliar',
    desc: 'Supervisa y controla la planta: mímicos, alarmas, tendencias.',
    audiencia: ['user', 'operator', 'admin'], ruta: null, suite: 'fluxai',
  },
  {
    sku: 'reactiva', nombre: 'Reactiva y penalización', icon: '', tier: 'premium', requiereContrato: true, capa: 'ami', grupo: 'ampliar',
    desc: 'Modelo CREG 015/2018 (Factor M, inductiva/capacitiva) con dato real de la planta.',
    audiencia: ['user', 'operator', 'admin'], ruta: null, suite: 'fluxai',
  },
  {
    sku: 'red', nombre: 'Red, balance y mapa', icon: '', tier: 'premium', requiereContrato: true, capa: 'ami', grupo: 'ampliar',
    desc: 'Topología, unifilar y balance de pérdidas por nivel de tensión (CREG 015/2018).',
    audiencia: ['user', 'operator', 'admin'], ruta: null, suite: 'fluxai',
  },
  {
    sku: 'billing_api', nombre: 'Integración por API / CIS', icon: '', tier: 'premium', requiereContrato: true, capa: 'ami', grupo: 'ampliar',
    desc: 'Conecta los datos de FluxAI a tu sistema comercial o de facturación.',
    audiencia: ['user', 'operator', 'admin'], ruta: null, suite: 'fluxai',
  },
  {
    sku: 'soporte_prioritario', nombre: 'Alex — asistente y soporte', icon: '', tier: 'premium', requiereContrato: true, capa: 'ami', grupo: 'ampliar', alex: true,
    desc: 'Asistente de la suite: eficiencia energética, PQR y soporte con respuesta al instante.',
    audiencia: ['user', 'technician', 'operator', 'admin'], ruta: null, suite: 'fluxai',
  },
  {
    sku: 'personalizacion_documentos', nombre: 'Personalización de formatos', icon: '', tier: 'premium', requiereContrato: true, capa: 'ami', grupo: 'ampliar',
    desc: 'Logo, colores y RUT en el membrete de facturas, actas y reportes.',
    audiencia: ['user', 'operator', 'admin'], ruta: null, suite: 'fluxai',
  },
  {
    sku: 'agua_gas', nombre: 'Agua y gas (multi-servicio)', icon: '', tier: 'gratis', capa: 'ami', grupo: 'ampliar',
    desc: 'La misma plataforma para medir también agua y gas, en un solo lugar.',
    audiencia: ['user', 'operator', 'admin'], ruta: null, suite: 'fluxai', disponible: false,
  },
  {
    sku: 'instalador_retie', nombre: 'Red de instaladores RETIE', icon: '', tier: 'gratis', capa: 'campo',
    desc: 'Técnicos (CONTE) e ingenieros (COPNIA) verificados; asignación por matching (rating·carga·experiencia).',
    audiencia: ['technician', 'admin'], ruta: 'instalador', suite: 'solarhub',
  },
  {
    sku: 'marketplace_b2b', nombre: 'Marketplace de proveedores', icon: '', tier: 'gratis', capa: 'campo',
    desc: 'Portal B2B: preferencial al 100%. En obra, 80% del técnico; 20% se reinvierte (infra, capa, herramientas).',
    audiencia: ['supplier', 'operator', 'admin'], ruta: 'proveedor', suite: 'solarhub',
  },
];

/** SKUs que Solar Hub no ofrece. Viven solo en FluxAI. */
export const EXCLUSIVOS_FLUXAI = Object.freeze(['almacenamiento_extra']);

export function esExclusivoFluxAI(sku) {
  return EXCLUSIVOS_FLUXAI.includes(String(sku || ''));
}

/** Servicio del catálogo único por SKU. */
export function servicioPorSku(sku) {
  return SERVICE_CATALOG.find((s) => s.sku === sku) || null;
}

/** ¿El SKU corresponde a un servicio premium? */
export function esPremium(sku) {
  const s = SERVICE_CATALOG.find((x) => x.sku === sku);
  return !!s && s.tier === 'premium';
}

/** Servicios visibles para una audiencia (null = invitado). */
export function serviciosParaAudiencia(aud) {
  return SERVICE_CATALOG.filter((s) =>
    !esExclusivoFluxAI(s.sku)
    && (s.audiencia.includes(aud) || (s.audiencia.includes(null) && aud == null)));
}

/** Rol de plataforma Solar Hub / FluxAI → audiencia del catálogo. */
export function serviciosPorCapa(lista) {
  const orden = ['publico', 'estudio', 'campo', 'ami'];
  const grupos = {};
  for (const s of lista || []) {
    const k = s.capa || (s.suite === 'fluxai' ? 'ami' : 'publico');
    (grupos[k] = grupos[k] || []).push(s);
  }
  return orden.filter((k) => grupos[k]?.length).map((k) => ({ capa: k, ...CAPAS_SERVICIO[k], items: grupos[k] }));
}

export function audienciaDeRolPlataforma(rol) {
  const r = String(rol ?? '').toLowerCase();
  if (r === 'admin' || r === 'soporte' || r === 'support' || r === 'super_administrator') return 'admin';
  if (r === 'tecnico' || r === 'technician') return 'technician';
  if (r === 'proveedor' || r === 'supplier') return 'supplier';
  if (r === 'operator' || r === 'operador' || r === 'administrator') return 'operator';
  return 'user';
}
